/* eslint-disable camelcase */
import React, { useState, useContext, useRef } from 'react';
import axios from 'axios';
import ModalComponent from 'vcomponents/ModalComponent';
import ModalContext from 'context/ModalContext';

// 글로벌 axios에는 JWT 인증 인터셉터(helpers/request.js)가 설치되어 KakaoAK 헤더를 Bearer로 덮어쓰므로,
// 외부(카카오) API는 인터셉터 없는 전용 인스턴스로 호출 (axios.create는 인터셉터를 상속하지 않음)
const externalClient = axios.create();

import { Form, Modal, Input, Radio, RadioChangeEvent, FormInstance } from 'antd';
import { useSaveAddressBook } from 'containers/Service/Quick/hooks';
import { CustomModal, AddressSearchWrapper, RadioGroupWrapper } from './styles';

const { Search } = Input;

interface IKaKaoAddress {
  address_name: string;
  y: string;
  x: string;
  address_type: string;
  address: {
    address_name: string;
    region_1depth_name: string;
    region_2depth_name: string;
    region_3depth_name: string;
    region_3depth_h_name: string;
    h_code: string;
    b_code: string;
    mountain_yn: string;
    main_address_no: string;
    sub_address_no: string;
    x: string;
    y: string;
  };
  road_address: {
    address_name: string;
    region_1depth_name: string;
    region_2depth_name: string;
    region_3depth_name: string;
    road_name: string;
    underground_yn: string;
    main_building_no: string;
    sub_building_no: string;
    building_name: string;
    zone_no: string;
    y: string;
    x: string;
  };
}

interface NaverAddressSearchModalProps {
  id?: string;
  open?: boolean;

  // eslint-disable-next-line no-unused-vars
  onChange?: (address: any) => void;
  // open 으로 직접 제어하는 경우 취소 시 부모가 닫을 수 있게 알린다
  onCancel?: () => void;
}

const MODAL_ID = 'naver-address-search';
export const NaverAddressSearchModal = ({ id = MODAL_ID, open, onChange, onCancel }: NaverAddressSearchModalProps) => {
  const context = useContext(ModalContext);
  const { hideModal } = context;

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [selectedAddress, setSelectedAddress] = useState<IKaKaoAddress[]>();
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();

  // 모달은 닫혀도 자식을 유지해 이전 검색어·결과가 남는다. 닫을 때마다 비워 매번 새로 검색하게 한다
  const resetSearch = () => {
    clearTimeout(searchTimer.current);
    setSearchKeyword('');
    setSelectedAddress(undefined);
  };
  // open 이 넘어온 경우(주소 관리)는 부모 state 가 모달을 제어하므로 hideModal 로는 닫히지 않는다. onCancel 로 부모에 위임한다
  const closeModal = () => {
    resetSearch();
    if (open === undefined) hideModal(id);
    else if (onCancel) onCancel();
  };

  const handleAddressBookClick = ({ target }: RadioChangeEvent) => {
    const address: IKaKaoAddress | undefined = target.value;

    try {
      if (!address || !onChange) return;

      // 카카오 검색 결과는 좌표·지번·도로명·우편번호를 이미 포함하므로 네이버 geocode 없이 그대로 매핑한다.
      // 도로명 없는 결과를 query 로 geocode 하면 네이버가 즉시 예외를 던져 선택·모달 닫기가 실행되지 않았다 (PROD-23766)
      onChange({
        zonecode: address.road_address?.zone_no || '',
        jibunAddress: address.address?.address_name || address.address_name,
        roadAddress: address.road_address?.address_name || '',
        gpslon: Number(address.x || 0),
        gpslat: Number(address.y || 0)
      });
    } finally {
      // 예외 발생 여부와 무관하게 검색 상태를 비운다. 모달 닫기는 onChange 를 받은 부모가 처리한다
      resetSearch();
      if (open === undefined) hideModal(id);
    }
  };

  const searchAddress = async (query: string) => {
    if (!query || !window?.naver) return;

    const headers = { Authorization: `KakaoAK 876d079e8902c2cce1df332acaeea6ef` };
    const params = { query };

    const { data } = await externalClient.get('https://dapi.kakao.com/v2/local/search/address.json', {
      headers,
      params
    });
    // 서버는 우편번호가 필수다. '봉은사로1길'처럼 건물번호 없는 도로명·지명 결과는 zone_no 가 비어 오므로 목록에서 제외한다
    const documents: IKaKaoAddress[] = data?.documents ?? [];
    setSelectedAddress(documents.filter((item) => item?.road_address?.zone_no));
  };

  // 글자마다 카카오 API 를 호출하지 않도록 입력이 0.3초 멈춘 뒤 한 번만 검색한다
  const handleAddressInputChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(target.value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchAddress(target.value), 300);
  };

  // 검색어가 그대로인 상태에서는 onChange 가 안 뜨므로 Enter 로도 다시 검색할 수 있게 한다
  const handlePressEnter = () => {
    clearTimeout(searchTimer.current);
    searchAddress(searchKeyword);
  };

  const getJibunAddress = (value: IKaKaoAddress) => {
    return value?.address
      ? `${value?.address?.region_3depth_name} ${value?.address?.main_address_no}-${value?.address?.sub_address_no}`
      : '';
  };

  return (
    <CustomModal
      visible={open ?? context.visible[id]}
      width={425}
      closable={false}
      mask={false}
      onCancel={closeModal}
      style={{ height: '550px' }}
      okButtonProps={{ style: { display: 'none' } }}
    >
      <AddressSearchWrapper>
        <div className="title">주소 검색</div>
        <Input
          allowClear
          value={searchKeyword}
          placeholder="예) 봉은사로 103길 5, 삼성동 108-12"
          onChange={handleAddressInputChange}
          onPressEnter={handlePressEnter}
        />
        <div className="title">주소 내역</div>
        {selectedAddress?.length ? (
          <RadioGroupWrapper onChange={handleAddressBookClick}>
            {selectedAddress?.map((item, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Radio.Button key={index} value={item}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ width: '70%', whiteSpace: 'break-spaces' }}>
                    {item?.road_address?.address_name || item?.address_name}
                    {item?.road_address?.building_name && `, ${item?.road_address?.building_name}`}
                  </span>
                  <span style={{ width: '30%', color: '#797C80', textAlign: 'end' }}>{getJibunAddress(item)}</span>
                </div>
              </Radio.Button>
            ))}
          </RadioGroupWrapper>
        ) : (
          <div className="description">
            <div className="sub-title">아래와 같은 조합으로 검색을 하시면 더욱 정확한 결과가 검색됩니다.</div>
            <div>도로명 + 건물번호</div>
            <div className="example">예) 봉은사로 103길 5, 충정로 23</div>
            <div>지역명(동/리) + 번지</div>
            <div className="example">예) 삼성동 108-12, 청평면 고성리 410-1</div>
          </div>
        )}
      </AddressSearchWrapper>
    </CustomModal>
  );
};

interface NaverAddressPostProps {
  form: FormInstance;
}

const NaverAddressPost = ({ form }: NaverAddressPostProps) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleOnOffAddressBook = () => setIsModalOpen(!isModalOpen);
  const handleAddressChange = (address: any) => {
    form.setFieldsValue({ ...address, isSaveAddress: true });
    handleOnOffAddressBook();
  };

  return (
    <Form layout="vertical" form={form}>
      <AddressSearchWrapper>
        <Form.Item rules={[{ required: true }]} label="주소" name="roadAddress">
          <Search
            readOnly
            allowClear
            placeholder="주소를 검색해주세요"
            onClick={handleOnOffAddressBook}
            onSearch={handleOnOffAddressBook}
          />
        </Form.Item>
        <Form.Item rules={[{ required: true, message: '상세 주소 입력해 주세요' }]} name="addressDetail">
          <Input allowClear placeholder="상세 주소를 입력해주세요" />
        </Form.Item>
        <Form.Item rules={[{ required: true }]} label="주소 별명" name="addressBookName">
          <Input allowClear placeholder="주소 별명을 입력해주세요" />
        </Form.Item>
        <Form.Item rules={[{ required: true }]} label="이름" name="name">
          <Input allowClear placeholder="이름을 입력해주세요" />
        </Form.Item>
        <Form.Item rules={[{ required: true }]} label="연락처" name="cellphone">
          <Input allowClear maxLength={11} placeholder="연락처를 입력해주세요" />
        </Form.Item>
      </AddressSearchWrapper>

      {/* Naver Address Search */}
      <NaverAddressSearchModal open={isModalOpen} onChange={handleAddressChange} onCancel={handleOnOffAddressBook} />
    </Form>
  );
};

const NaverAddressPostModal = () => {
  const [form] = Form.useForm();
  const context = useContext(ModalContext);

  const { mutateAsync: saveAddressBook } = useSaveAddressBook();

  const handleSubmit = async () => {
    const params = form.getFieldsValue([
      'jibunAddress',
      'roadAddress',
      'addressDetail',
      'addressBookName',
      'zonecode',
      'gpslon',
      'gpslat',
      'name',
      'cellphone',
      'isSaveAddress'
    ]);

    await saveAddressBook(params);
    context.hideModal('naver-address-modal');
    Modal.success({
      title: '저장되었습니다',
      okText: '확인',
      cancelButtonProps: { style: { display: 'none' } }
    });
  };

  return (
    <ModalComponent
      id="naver-address-modal"
      width={425}
      // 공통 컴포넌트에 필수 값 구분이 안되어 있음
      // @ts-ignore
      component={<NaverAddressPost form={form} />}
      okText="추가"
      onOk={handleSubmit}
      // form 은 모달 밖(useForm)에 살아 있어 destroyOnClose 로 폼이 언마운트돼도 값이 남는다. 추가·취소 어느 쪽으로 닫혀도 비운다
      afterClose={() => form.resetFields()}
      style={{ height: '550px' }}
    />
  );
};

export default NaverAddressPostModal;
